// Copyright (c) 2026 TRIVONT IT Private Limited. All rights reserved.

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle, Settings, GitBranch, CheckCircle,
  Plus, BarChart3, Loader2, TrendingUp, Users, AlertTriangle,
  ClipboardCheck, Activity, Zap, ChevronDown, RefreshCw
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend
} from 'recharts';
import { reportAPI, incidentAPI, serviceRequestAPI, changeRequestAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const stClass = {
  new: 'bg-slate-100 text-slate-700', assigned: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-emerald-100 text-emerald-800', pending_user: 'bg-amber-100 text-amber-800',
  pending_approval: 'bg-purple-100 text-purple-800', resolved: 'bg-emerald-200 text-emerald-900',
  closed: 'bg-slate-200 text-slate-600', reopened: 'bg-orange-100 text-orange-700',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-xl shadow-lg px-4 py-3 text-xs">
      <p className="font-semibold text-slate-700 dark:text-slate-200 mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-slate-500 dark:text-slate-400">{p.name}:</span>
          <span className="font-medium text-slate-700 dark:text-slate-200">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

const RADIAN = Math.PI / 180;
const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.06) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  return (
    <text x={cx + r * Math.cos(-midAngle * RADIAN)} y={cy + r * Math.sin(-midAngle * RADIAN)}
      fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight="600">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// ─── Control Components ────────────────────────────────────────────────────
function SectionHeader({ icon: Icon, title, iconColor, children }) {
  return (
    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
      <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2">
        <Icon className={`w-4 h-4 ${iconColor}`} /> {title}
      </h3>
      <div className="flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}

function PeriodSelect({ value, onChange }) {
  return (
    <div className="flex items-center gap-1.5">
      <select value={value.preset} onChange={e => onChange({ ...value, preset: e.target.value })}
        className="text-xs border dark:border-slate-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-slate-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-sky-500 cursor-pointer">
        <option value="7">Last 1 week</option>
        <option value="14">Last 2 weeks</option>
        <option value="30">Last 1 month</option>
        <option value="90">Last 3 months</option>
        <option value="custom">Custom</option>
      </select>
      {value.preset === 'custom' && (
        <>
          <input type="date" value={value.start} onChange={e => onChange({ ...value, start: e.target.value })}
            className="text-xs border dark:border-slate-600 rounded-lg px-2 py-1.5 bg-white dark:bg-slate-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-sky-500" />
          <span className="text-xs text-slate-400">to</span>
          <input type="date" value={value.end} onChange={e => onChange({ ...value, end: e.target.value })}
            className="text-xs border dark:border-slate-600 rounded-lg px-2 py-1.5 bg-white dark:bg-slate-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-sky-500" />
        </>
      )}
    </div>
  );
}

function CountSelect({ value, onChange, label = 'Top', options = [10, 20, 30] }) {
  return (
    <div className="flex items-center gap-1.5">
      <select value={value.preset} onChange={e => onChange({ ...value, preset: e.target.value })}
        className="text-xs border dark:border-slate-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-slate-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-sky-500 cursor-pointer">
        {options.map(n => <option key={n} value={n}>{label} {n}</option>)}
        <option value="custom">Custom</option>
      </select>
      {value.preset === 'custom' && (
        <input type="number" min="1" max="100" value={value.custom}
          onChange={e => onChange({ ...value, custom: e.target.value })}
          className="text-xs border dark:border-slate-600 rounded-lg px-2 py-1.5 w-16 bg-white dark:bg-slate-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-sky-500" />
      )}
    </div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, subColor, icon: Icon, iconBg, iconColor, border }) {
  return (
    <div className={`bg-white dark:bg-slate-800 p-5 rounded-2xl border-l-4 ${border} shadow-sm`}>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</span>
          <div className="text-3xl font-bold text-slate-800 dark:text-white mt-1 mb-0.5">{value ?? '—'}</div>
          <span className={`text-xs font-medium ${subColor}`}>{sub}</span>
        </div>
        <div className={`w-12 h-12 ${iconBg} rounded-2xl flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
      </div>
    </div>
  );
}

// ─── Recent Ticket Card ───────────────────────────────────────────────────────
function RecentTicketCard({ title, icon: Icon, iconColor, tickets, navigate, type, tagColor, count, onCountChange }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border dark:border-slate-700 shadow-sm p-5">
      <SectionHeader icon={Icon} title={title} iconColor={iconColor}>
        <CountSelect
          value={count}
          onChange={onCountChange}
          label="Last"
          options={[10, 20, 30]}
        />
      </SectionHeader>
      {tickets.length === 0 ? (
        <p className="text-slate-400 text-sm py-4 text-center">No {title.toLowerCase()} yet.</p>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {tickets.map(t => (
            <div key={t.id} onClick={() => navigate(`/ticket/${type}/${t.id}`)}
              className="flex justify-between items-center p-2.5 bg-slate-50 dark:bg-slate-900 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-all">
              <span className="flex items-center gap-2 min-w-0">
                <span className={`font-mono ${tagColor} px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0`}>{t.ticket_number}</span>
                <span className="text-sm truncate dark:text-slate-200">{t.title}</span>
              </span>
              <span className={`${stClass[t.status] || 'bg-slate-100 text-slate-700'} px-2 py-0.5 rounded-full text-xs flex-shrink-0 ml-2`}>
                {t.status?.replace(/_/g, ' ')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── End User Dashboard ───────────────────────────────────────────────────────
function EndUserDashboard({ stats, recentIncidents, recentSRs, loading, navigate }) {
  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        <StatCard label="Open Incidents" value={stats?.open_incidents} sub={`${stats?.breached_incidents || 0} breached`} subColor="text-rose-500" icon={AlertCircle} iconBg="bg-rose-100" iconColor="text-rose-600" border="border-l-rose-500" />
        <StatCard label="Service Requests" value={stats?.open_service_requests} sub="open requests" subColor="text-blue-500" icon={Settings} iconBg="bg-blue-100" iconColor="text-blue-600" border="border-l-blue-500" />
        <StatCard label="SLA Compliance" value={`${stats?.sla_compliance || 0}%`} sub="compliance rate" subColor="text-emerald-500" icon={CheckCircle} iconBg="bg-emerald-100" iconColor="text-emerald-600" border="border-l-emerald-500" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <RecentTicketCard title="Recent Incidents" icon={AlertCircle} iconColor="text-rose-500" tickets={recentIncidents} navigate={navigate} type="incident" tagColor="bg-rose-100 text-rose-800" count={{ preset: '5' }} onCountChange={() => {}} />
        <RecentTicketCard title="Recent Service Requests" icon={Settings} iconColor="text-blue-500" tickets={recentSRs} navigate={navigate} type="sr" tagColor="bg-blue-100 text-blue-800" count={{ preset: '5' }} onCountChange={() => {}} />
      </div>
    </>
  );
}

// ─── Analytics Dashboard ─────────────────────────────────────────────────────
function AnalyticsDashboard({ stats, statsLoading, recentIncidents, recentSRs, recentCRs, navigate,
  volumePeriod, setVolumePeriod, breachPeriod, setBreachPeriod,
  workloadCount, setWorkloadCount,
  incCount, setIncCount, srCount, setSrCount, crCount, setCrCount }) {

  if (statsLoading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;

  return (
    <>
      {/* Row 1 — KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard label="Open Incidents" value={stats?.open_incidents} sub={`${stats?.breached_incidents || 0} SLA breached`} subColor="text-rose-500" icon={AlertCircle} iconBg="bg-rose-100" iconColor="text-rose-600" border="border-l-rose-500" />
        <StatCard label="Service Requests" value={stats?.open_service_requests} sub="open requests" subColor="text-blue-500" icon={Settings} iconBg="bg-blue-100" iconColor="text-blue-600" border="border-l-blue-500" />
        <StatCard label="Change Requests" value={stats?.open_change_requests} sub={`${stats?.pending_approval_crs || 0} pending`} subColor="text-amber-500" icon={GitBranch} iconBg="bg-purple-100" iconColor="text-purple-600" border="border-l-purple-500" />
        <StatCard label="Pending Approvals" value={stats?.pending_approvals} sub="SR + CR awaiting" subColor="text-violet-500" icon={ClipboardCheck} iconBg="bg-violet-100" iconColor="text-violet-600" border="border-l-violet-500" />
        <StatCard label="SLA Compliance" value={`${stats?.sla_compliance || 0}%`} sub="resolution rate"
          subColor={stats?.sla_compliance >= 90 ? 'text-emerald-500' : stats?.sla_compliance >= 70 ? 'text-amber-500' : 'text-rose-500'}
          icon={CheckCircle} iconBg="bg-emerald-100" iconColor="text-emerald-600" border="border-l-emerald-500" />
      </div>

      {/* Row 2 — Volume Trend + Priority Donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-2xl border dark:border-slate-700 shadow-sm p-5">
          <SectionHeader icon={TrendingUp} title="Ticket Volume" iconColor="text-sky-500">
            <PeriodSelect value={volumePeriod} onChange={setVolumePeriod} />
          </SectionHeader>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={stats?.volume_trend || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                {[['incGrad','#EF4444'],['srGrad','#3B82F6'],['crGrad','#8B5CF6']].map(([id, c]) => (
                  <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={c} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={c} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="Incidents" stroke="#EF4444" fill="url(#incGrad)" strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="SR" stroke="#3B82F6" fill="url(#srGrad)" strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="CR" stroke="#8B5CF6" fill="url(#crGrad)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-2xl border dark:border-slate-700 shadow-sm p-5">
          <SectionHeader icon={AlertCircle} title="Open Incident Priority" iconColor="text-rose-500" />
          {(stats?.priority_breakdown || []).every(p => p.value === 0) ? (
            <div className="flex items-center justify-center h-48 text-slate-400 text-sm">No open incidents</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={stats?.priority_breakdown || []} cx="50%" cy="50%" innerRadius={45} outerRadius={75}
                    dataKey="value" labelLine={false} label={PieLabel}>
                    {(stats?.priority_breakdown || []).map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-1.5 mt-2">
                {(stats?.priority_breakdown || []).map(p => (
                  <div key={p.name} className="flex items-center gap-1.5 text-xs">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: p.color }} />
                    <span className="text-slate-500 dark:text-slate-400 truncate">{p.name}</span>
                    <span className="font-bold text-slate-700 dark:text-slate-200 ml-auto">{p.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Row 3 — Status Distribution + Top Applications */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <div className="bg-white dark:bg-slate-800 rounded-2xl border dark:border-slate-700 shadow-sm p-5">
          <SectionHeader icon={Activity} title="Ticket Status Distribution (INC + SR + CR)" iconColor="text-sky-500" />
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stats?.status_distribution || []} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Tickets" radius={[4, 4, 0, 0]}>
                {(stats?.status_distribution || []).map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-2xl border dark:border-slate-700 shadow-sm p-5">
          <SectionHeader icon={Zap} title="Top Applications by Open Tickets" iconColor="text-amber-500" />
          {(stats?.top_applications || []).length === 0 ? (
            <div className="flex items-center justify-center h-48 text-slate-400 text-sm">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats?.top_applications || []} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} width={120} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="tickets" name="Open Tickets" fill="#0EA5E9" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Row 4 — Assignee Workload + SLA Breach Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <div className="bg-white dark:bg-slate-800 rounded-2xl border dark:border-slate-700 shadow-sm p-5">
          <SectionHeader icon={Users} title="Agent Workload — Open Tickets" iconColor="text-violet-500">
            <CountSelect value={workloadCount} onChange={setWorkloadCount} options={[10, 20, 30]} />
          </SectionHeader>
          {(stats?.assignee_workload || []).length === 0 ? (
            <div className="flex items-center justify-center h-40 text-slate-400 text-sm">No assigned tickets yet</div>
          ) : (
            <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
              {(stats?.assignee_workload || []).map((agent, i) => {
                const maxOpen = Math.max(...(stats?.assignee_workload || []).map(a => a.open), 1);
                const pct = Math.round((agent.open / maxOpen) * 100);
                const color = pct > 75 ? 'bg-rose-500' : pct > 40 ? 'bg-amber-500' : 'bg-emerald-500';
                return (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-slate-700 dark:text-slate-200 truncate">{agent.name || 'Unassigned'}</span>
                      <span className="font-bold text-slate-500 dark:text-slate-400 ml-2 flex-shrink-0">{agent.open} open</span>
                    </div>
                    <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-2xl border dark:border-slate-700 shadow-sm p-5">
          <SectionHeader icon={AlertTriangle} title="SLA Breaches" iconColor="text-rose-500">
            <PeriodSelect value={breachPeriod} onChange={setBreachPeriod} />
          </SectionHeader>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stats?.breach_trend || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="breaches" name="Breaches" fill="#EF4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 5 — Recent Tickets (3 columns) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <RecentTicketCard title="Recent Incidents" icon={AlertCircle} iconColor="text-rose-500"
          tickets={recentIncidents} navigate={navigate} type="incident" tagColor="bg-rose-100 text-rose-800"
          count={incCount} onCountChange={setIncCount} />
        <RecentTicketCard title="Recent Service Requests" icon={Settings} iconColor="text-blue-500"
          tickets={recentSRs} navigate={navigate} type="sr" tagColor="bg-blue-100 text-blue-800"
          count={srCount} onCountChange={setSrCount} />
        <RecentTicketCard title="Recent Change Requests" icon={GitBranch} iconColor="text-purple-500"
          tickets={recentCRs} navigate={navigate} type="cr" tagColor="bg-purple-100 text-purple-800"
          count={crCount} onCountChange={setCrCount} />
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEndUser = (user?.role_name || '').toLowerCase().replace(/\s+/g, '_') === 'end_user';

  const today = new Date().toISOString().slice(0, 10);
  const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

  // ── Stats controls ──────────────────────────────────────────────────────────
  const [volumePeriod, setVolumePeriod]   = useState({ preset: '14', start: daysAgo(13), end: today });
  const [breachPeriod, setBreachPeriod]   = useState({ preset: '7',  start: daysAgo(6),  end: today });
  const [workloadCount, setWorkloadCount] = useState({ preset: '10', custom: '10' });

  // ── Recent ticket controls ──────────────────────────────────────────────────
  const [incCount, setIncCount] = useState({ preset: '10', custom: '10' });
  const [srCount,  setSrCount]  = useState({ preset: '10', custom: '10' });
  const [crCount,  setCrCount]  = useState({ preset: '10', custom: '10' });

  // ── Data ────────────────────────────────────────────────────────────────────
  const [stats, setStats]                 = useState(null);
  const [statsLoading, setStatsLoading]   = useState(true);
  const [recentIncidents, setRecentIncidents] = useState([]);
  const [recentSRs,  setRecentSRs]        = useState([]);
  const [recentCRs,  setRecentCRs]        = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);

  // Resolve count value (handles preset + custom)
  const resolveCount = (ctrl) => ctrl.preset === 'custom' ? (parseInt(ctrl.custom) || 10) : parseInt(ctrl.preset);

  // ── Fetch analytics stats ───────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    const params = {};

    if (volumePeriod.preset === 'custom') {
      if (volumePeriod.start) params.volume_start = volumePeriod.start;
      if (volumePeriod.end)   params.volume_end   = volumePeriod.end;
    } else {
      params.volume_days = volumePeriod.preset;
    }

    if (breachPeriod.preset === 'custom') {
      if (breachPeriod.start) params.breach_start = breachPeriod.start;
      if (breachPeriod.end)   params.breach_end   = breachPeriod.end;
    } else {
      params.breach_days = breachPeriod.preset;
    }

    params.workload_limit = resolveCount(workloadCount);

    try {
      const res = await reportAPI.dashboard(params);
      setStats(res.data);
    } catch {}
    finally { setStatsLoading(false); }
  }, [volumePeriod, breachPeriod, workloadCount]); // eslint-disable-line

  // ── Fetch recent tickets ────────────────────────────────────────────────────
  const fetchRecent = useCallback(async () => {
    setTicketsLoading(true);
    try {
      const [incRes, srRes, crRes] = await Promise.allSettled([
        incidentAPI.list({ ordering: '-created_at', page_size: resolveCount(incCount) }),
        serviceRequestAPI.list({ ordering: '-created_at', page_size: resolveCount(srCount) }),
        changeRequestAPI.list({ ordering: '-created_at', page_size: resolveCount(crCount) }),
      ]);
      if (incRes.status === 'fulfilled') setRecentIncidents(incRes.value.data?.results || incRes.value.data || []);
      if (srRes.status === 'fulfilled')  setRecentSRs(srRes.value.data?.results  || srRes.value.data  || []);
      if (crRes.status === 'fulfilled')  setRecentCRs(crRes.value.data?.results  || crRes.value.data  || []);
    } catch {}
    finally { setTicketsLoading(false); }
  }, [incCount, srCount, crCount]); // eslint-disable-line

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchRecent(); }, [fetchRecent]);

  // Initial load for end-users (simpler)
  const [euLoading, setEuLoading] = useState(true);
  useEffect(() => {
    if (!isEndUser) return;
    const load = async () => {
      try {
        const [dRes, incRes, srRes] = await Promise.allSettled([
          reportAPI.dashboard(),
          incidentAPI.list({ ordering: '-created_at', page_size: 5 }),
          serviceRequestAPI.list({ ordering: '-created_at', page_size: 5 }),
        ]);
        if (dRes.status === 'fulfilled')   setStats(dRes.value.data);
        if (incRes.status === 'fulfilled') setRecentIncidents(incRes.value.data?.results || incRes.value.data || []);
        if (srRes.status === 'fulfilled')  setRecentSRs(srRes.value.data?.results || srRes.value.data || []);
      } catch {}
      finally { setEuLoading(false); }
    };
    load();
  }, [isEndUser]);

  return (
    <div data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-sky-500" /> TRIVONT SupportOne · Dashboard
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {isEndUser ? 'Your tickets at a glance' : 'ITIL-aligned analytics · Real-time insights'}
          </p>
        </div>
        <div className="flex items-center gap-2 mt-4 md:mt-0">
          {!isEndUser && (
            <button onClick={() => { fetchStats(); fetchRecent(); }}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm bg-white dark:bg-slate-800 border dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 transition-all"
              title="Refresh dashboard">
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => navigate('/new-request')}
            className="bg-sky-500 hover:bg-sky-600 text-white px-5 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 transition-all"
            data-testid="new-request-btn">
            <Plus className="w-4 h-4" /> New Request
          </button>
        </div>
      </div>

      {isEndUser
        ? <EndUserDashboard stats={stats} recentIncidents={recentIncidents} recentSRs={recentSRs} loading={euLoading} navigate={navigate} />
        : <AnalyticsDashboard
            stats={stats} statsLoading={statsLoading}
            recentIncidents={recentIncidents} recentSRs={recentSRs} recentCRs={recentCRs}
            navigate={navigate}
            volumePeriod={volumePeriod} setVolumePeriod={setVolumePeriod}
            breachPeriod={breachPeriod} setBreachPeriod={setBreachPeriod}
            workloadCount={workloadCount} setWorkloadCount={setWorkloadCount}
            incCount={incCount} setIncCount={setIncCount}
            srCount={srCount} setSrCount={setSrCount}
            crCount={crCount} setCrCount={setCrCount}
          />
      }
    </div>
  );
}
